import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Sparkles, Loader2, Calendar, User, ListChecks } from "lucide-react";
import { workPlannerApi } from "@/services/workPlannerApi";
import { mpApi, SERVICE_KEYS, serviceMeta, STATUS_KEYS, statusMeta, bucketOf, isOverdue, ProgressBar, pctOf, monthLabel, fmtDay, ServiceChip } from "./mpShared";

/** Slide-over with one client's month: per-service cards and the tasks behind them. */
export default function ClientPanel({ client, initialService, month, businessId, members, canManage, onClose, openTask, newTask, refresh }) {
  const [tasks, setTasks] = useState(null);
  const [service, setService] = useState(initialService || "");
  const [generating, setGenerating] = useState(false);
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);

  const load = useCallback(() => {
    return mpApi("/tasks", { params: { month, client_id: client.client_id, business_id: businessId || undefined } })
      .then((d) => setTasks(d.tasks)).catch((e) => { toast.error(e.message); setTasks([]); });
  }, [month, client.client_id, businessId]);
  useEffect(() => { load(); }, [load]);

  // Service cards from live tasks, so status changes here update immediately.
  const cards = useMemo(() => {
    const out = {};
    for (const k of SERVICE_KEYS) out[k] = { target: client.targets[k] || 0, total: 0, completed: 0, in_progress: 0, overdue: 0 };
    for (const t of tasks || []) {
      if (!out[t.service]) continue;
      const c = out[t.service]; const b = bucketOf(t.status);
      c.total += 1;
      if (b === "completed") c.completed += 1; else if (b === "in_progress") c.in_progress += 1;
      if (isOverdue(t)) c.overdue += 1;
    }
    return out;
  }, [tasks, client.targets]);
  const sum = useMemo(() => {
    const target = SERVICE_KEYS.reduce((s, k) => s + cards[k].target, 0);
    const completed = SERVICE_KEYS.reduce((s, k) => s + cards[k].completed, 0);
    const inProg = SERVICE_KEYS.reduce((s, k) => s + cards[k].in_progress, 0);
    const overdue = SERVICE_KEYS.reduce((s, k) => s + cards[k].overdue, 0);
    return { target, completed, inProg, overdue, pending: Math.max(0, target - completed - inProg), pct: pctOf(completed, target) };
  }, [cards]);

  const visible = (tasks || []).filter((t) => !service || (service === "none" ? !SERVICE_KEYS.includes(t.service) : t.service === service));

  const changeStatus = async (task, status) => {
    setTasks((prev) => prev.map((x) => (x._id === task._id ? { ...x, status } : x)));
    try { await workPlannerApi.updateTask(task.task_id || task._id, { status }); refresh(); }
    catch (e) { toast.error(e.message); load(); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await mpApi("/generate", { method: "POST", body: { month, client_id: client.client_id, business_id: businessId || undefined } });
      toast.success(r.created ? `${r.created} task slot${r.created === 1 ? "" : "s"} created` : "All targets already have tasks");
      await load(); refresh();
    } catch (e) { toast.error(e.message); } finally { setGenerating(false); }
  };

  const missing = SERVICE_KEYS.reduce((s, k) => s + Math.max(0, cards[k].target - cards[k].total), 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-2xl flex-col bg-slate-50 shadow-2xl" data-testid="mp-client-panel">
        <div className="border-b border-slate-200 bg-white px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{client.name}</h2>
              <p className="text-sm text-slate-500">{monthLabel(month)}</p>
            </div>
            <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {[["Target", sum.target, "text-slate-900"], ["Completed", sum.completed, "text-green-600"], ["In Progress", sum.inProg, "text-blue-600"],
              ["Pending", sum.pending, "text-amber-600"], ["Overdue", sum.overdue, "text-rose-600"], ["Done", `${sum.pct}%`, "text-indigo-600"]].map(([l, v, c]) => (
              <div key={l} className="rounded-lg bg-slate-50 px-2 py-1.5">
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{l}</div>
                <div className={`text-lg font-semibold ${c}`}>{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-2"><ProgressBar pct={sum.pct} color="#6366f1" /></div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Service cards */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {SERVICE_KEYS.filter((k) => cards[k].target > 0 || cards[k].total > 0).map((k) => {
              const c = cards[k]; const m = serviceMeta(k);
              const active = service === k;
              return (
                <button key={k} onClick={() => setService(active ? "" : k)}
                  className={`rounded-xl border bg-white p-3 text-left transition ${active ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: m.color }} />{m.label}</div>
                  <div className="mt-1.5 flex items-baseline gap-1">
                    <span className="text-xl font-semibold text-slate-900">{c.completed}</span>
                    <span className="text-xs text-slate-500">of {c.target} done</span>
                  </div>
                  <div className="mt-1.5"><ProgressBar pct={pctOf(c.completed, c.target)} color={m.color} /></div>
                  <div className="mt-1.5 text-[11px] text-slate-500">
                    {Math.max(0, c.target - c.completed)} pending{c.overdue ? <span className="text-rose-600"> · {c.overdue} overdue</span> : ""}
                    {c.total < c.target && <span className="text-amber-600"> · {c.target - c.total} not created</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={() => newTask({ client_id: client.client_id, service: service && service !== "none" ? service : "" })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"><Plus className="h-4 w-4" /> Add Task</button>
            {canManage && missing > 0 && (
              <button onClick={generate} disabled={generating}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate {missing} missing task{missing === 1 ? "" : "s"}
              </button>
            )}
            <button onClick={() => setService("")} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${!service ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
              <ListChecks className="h-4 w-4" /> View all tasks ({(tasks || []).length})
            </button>
          </div>

          {/* Task list */}
          <div className="mt-3 space-y-2">
            {tasks === null ? <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
              : visible.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-500">No tasks{service ? ` for ${serviceMeta(service).label}` : ""} yet.</div>
              : visible.map((t) => (
                <div key={t._id} className={`rounded-xl border bg-white p-3 ${isOverdue(t) ? "border-rose-200" : "border-slate-200"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => openTask(t)} className="min-w-0 text-left">
                      <div className="flex items-center gap-1.5">
                        <ServiceChip service={t.service} />
                        <span className="truncate text-sm font-medium text-slate-900 hover:text-indigo-700">{t.title}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className={`inline-flex items-center gap-1 ${isOverdue(t) ? "font-semibold text-rose-600" : ""}`}><Calendar className="h-3 w-3" />{fmtDay(t.due_date)}{isOverdue(t) ? " · overdue" : ""}</span>
                        <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{(t.assigned_users || []).map((u) => memberName[u]).filter(Boolean).join(", ") || "Unassigned"}</span>
                        {t.priority && t.priority !== "medium" && <span className="capitalize">{t.priority}</span>}
                      </div>
                    </button>
                    <select value={STATUS_KEYS.includes(t.status) ? t.status : ""} onChange={(e) => changeStatus(t, e.target.value)}
                      className={`shrink-0 cursor-pointer rounded-full px-2 py-1 text-[11px] font-medium ring-1 ring-inset outline-none ${statusMeta(t.status).chip}`}>
                      {!STATUS_KEYS.includes(t.status) && <option value="">{statusMeta(t.status).label}</option>}
                      {STATUS_KEYS.map((k) => <option key={k} value={k}>{statusMeta(k).label}</option>)}
                    </select>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
